import React, { useState } from 'react';
import { apiService } from '../services/api';

interface ModernInvoicePrintButtonProps {
  invoiceId: string;
  language?: 'en' | 'de';
  variant?: 'download' | 'open' | 'both';
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  title?: string;
  disabled?: boolean;
}

/**
 * Modern Invoice Print Button Component
 * 
 * Provides options to download or open modern invoice PDFs
 * with professional styling and multi-language support
 */
const ModernInvoicePrintButton: React.FC<ModernInvoicePrintButtonProps> = ({
  invoiceId,
  language,
  variant = 'both',
  className = '',
  style = {},
  children,
  title,
  disabled = false
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (disabled || loading) return;

    setLoading(true);
    setError(null);

    try {
      const filename = `modern-invoice-${invoiceId}${language ? `-${language}` : ''}.pdf`;
      await apiService.downloadModernPDF(invoiceId, language, filename);
    } catch (err: any) {
      console.error('Error downloading modern invoice PDF:', err);
      setError(err.message || 'Failed to download PDF');
      
      // Show error to user
      alert(`Failed to download PDF: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = async () => {
    if (disabled || loading) return;

    setLoading(true);
    setError(null);

    try {
      await apiService.openModernPDF(invoiceId, language);
    } catch (err: any) {
      console.error('Error opening modern invoice PDF:', err);
      setError(err.message || 'Failed to open PDF');
      
      // Show error to user
      alert(`Failed to open PDF: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    if (loading) return 'Generating...';
    if (children) return children;
    
    switch (variant) {
      case 'download':
        return '📥 Download Modern';
      case 'open':
        return '🔍 View Modern';
      case 'both':
      default:
        return '📄 Modern PDF';
    }
  };

  const getButtonClass = () => {
    const baseClass = 'btn';
    const variantClass = error ? 'btn-danger' : 'btn-primary';
    const loadingClass = loading ? 'loading' : '';
    const disabledClass = disabled ? 'disabled' : '';
    
    return `${baseClass} ${variantClass} ${loadingClass} ${disabledClass} ${className}`.trim();
  };

  const buttonStyle = {
    position: 'relative' as const,
    ...style
  };

  if (variant === 'both') {
    return (
      <div className="btn-group" style={style}>
        <button
          className={getButtonClass()}
          onClick={handleOpen}
          disabled={disabled || loading}
          title={title || `Open modern invoice PDF${language ? ` (${language.toUpperCase()})` : ''}`}
          style={{ marginRight: '2px' }}
        >
          {loading ? '⏳' : '🔍'} View Modern
        </button>
        <button
          className={getButtonClass()}
          onClick={handleDownload}
          disabled={disabled || loading}
          title={title || `Download modern invoice PDF${language ? ` (${language.toUpperCase()})` : ''}`}
        >
          {loading ? '⏳' : '📥'} Download
        </button>
      </div>
    );
  }

  return (
    <button
      className={getButtonClass()}
      onClick={variant === 'download' ? handleDownload : handleOpen}
      disabled={disabled || loading}
      title={title || `${variant === 'download' ? 'Download' : 'Open'} modern invoice PDF${language ? ` (${language.toUpperCase()})` : ''}`}
      style={buttonStyle}
    >
      {getButtonText()}
    </button>
  );
};

export default ModernInvoicePrintButton;